import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { UserAuthContextProvider } from "./components/context/UserAuthContext";
import App from "./App";
import { lightTheme, darkTheme } from "./theme";

// Create a context for theme toggling
const ThemeContext = React.createContext();

const Root = () => {
  const [mode, setMode] = useState(() => {
    // Check localStorage first
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) return savedMode;
    // Then check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? 'dark' 
      : 'light';
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Memoize the theme to prevent unnecessary re-renders
  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode]);

  // Function to toggle theme
  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  return (
    <ThemeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <UserAuthContextProvider>
          <App />
        </UserAuthContextProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Export ThemeContext for use in other components
export { ThemeContext };